import type { BasketItem } from '../types/order';

export type DisplayFrame = [string, string];
export const DISPLAY_WELCOME: DisplayFrame = ['XENTRIX FLOW', 'ΚΑΛΩΣ ΗΡΘΑΤΕ'];

export function displayMoney(value: number): string {
  return Number.isFinite(value) && value >= 0 && value < 1000000 ? value.toFixed(2).replace('.', ',') : '---';
}

function amountLine(label: string, amount: number, currency = true): string {
  const value = `${displayMoney(amount)}${currency ? ' EUR' : ''}`;
  const room = Math.max(0, 19 - value.length);
  // Serial control bytes must never become commands, even in a product name.
  // eslint-disable-next-line no-control-regex
  const clean = label.normalize('NFC').replace(/[\x00-\x1f\x7f-\x9f]/g, ' ').replace(/€/g, 'EUR');
  return `${clean.slice(0, room).padEnd(20 - value.length)}${value}`;
}

function cashChange(total: number, received?: number): number | undefined {
  if (received == null || !Number.isFinite(received) || !Number.isFinite(total) || total < 0 || received < 0) return undefined;
  const changeCents = Math.round(received * 100) - Math.round(total * 100);
  return changeCents >= 0 ? changeCents / 100 : undefined;
}

export function changedDisplayItem(previous: BasketItem[], current: BasketItem[], activeId?: string): BasketItem | undefined {
  const signature = (item: BasketItem) => JSON.stringify([item.quantity, item.totalPrice, item.product.name, item.selectedSize?.name]);
  const before = new Map(previous.map(item => [item.id, signature(item)]));
  // Automatically added environmental fees must not hide the purchased item.
  const products = current.filter(item => item.product.fee_category == null);
  const candidates = products.length ? products : current;
  return candidates.find(item => before.get(item.id) !== signature(item))
    || candidates.find(item => item.id === activeId) || candidates[candidates.length - 1];
}

/**
 * `totalLabel`: «ΥΠΟΛΟΙΠΟ» όταν αφαιρείται προκαταβολή πελάτη — τότε το `total` είναι ό,τι μένει να
 * πληρωθεί (και τα ρέστα βγαίνουν από αυτό), όχι το σύνολο της παραγγελίας.
 */
export function saleDisplayFrame(item: BasketItem | undefined, total: number, checkout: boolean, cashReceived?: number, totalLabel = 'ΣΥΝΟΛΟ'): DisplayFrame {
  if (!item) return DISPLAY_WELCOME;
  if (checkout) {
    const change = cashChange(total, cashReceived);
    return [amountLine(totalLabel, total), change != null ? amountLine('ΡΕΣΤΑ', change) : 'ΠΡΟΣ ΠΛΗΡΩΜΗ'];
  }
  const name = `${item.quantity !== 1 ? `${item.quantity}x ` : ''}${item.product.name}`;
  return [amountLine(name, item.totalPrice * item.quantity, false), amountLine(totalLabel, total)];
}

export function completedDisplayFrame(total: number, cashReceived?: number): DisplayFrame {
  const change = cashChange(total, cashReceived);
  return ['ΕΥΧΑΡΙΣΤΟΥΜΕ!', change != null ? amountLine('ΡΕΣΤΑ', change) : amountLine('ΣΥΝΟΛΟ', total)];
}
